// Tiny promise-based wrapper over native IndexedDB.
//
// One database, `tkos-offline`, version 1. Object stores are configured per
// record type with `id` as keyPath. A built-in `_sync_queue` store is always
// created so queued operations survive page reloads.
//
// Deliberately minimal: no third-party deps (no idb/idb-keyval/dexie). Every
// operation opens a transaction, runs the op, awaits `oncomplete`, resolves.

export interface TkosDb {
  /** Fetch every record in `store`. */
  getAll<T>(store: string): Promise<T[]>;
  /** Fetch one record by id; resolves `undefined` if missing. */
  get<T>(store: string, id: string): Promise<T | undefined>;
  /** Upsert a record. Requires the record to have an `id` property. */
  put<T>(store: string, record: T): Promise<void>;
  /** Upsert many records in a single transaction. */
  putMany<T>(store: string, records: T[]): Promise<void>;
  /** Delete a record by id. No-ops if it doesn't exist. */
  remove(store: string, id: string): Promise<void>;
  /** Remove every record from `store`. */
  clear(store: string): Promise<void>;
}

const DB_NAME = "tkos-offline";
const QUEUE_STORE = "_sync_queue";

/**
 * Open the shared `tkos-offline` database, ensuring the requested object
 * stores (and the always-present `_sync_queue` store) exist.
 *
 * Each page declares its own store set (contacts, submissions, calendar, …),
 * so the schema GROWS over time as pages are visited. We therefore can't use a
 * fixed version: opening at a fixed version means the stores are only ever
 * created by whichever page is visited first, and later pages whose store is
 * missing fail forever. Instead we open at the DB's CURRENT version, and if any
 * wanted store is missing we reopen at `version + 1` so `onupgradeneeded` fires
 * and creates the missing stores. Additive and data-safe — existing stores are
 * never dropped or touched.
 *
 * Safe to call concurrently — IndexedDB serialises opens.
 */
export function openDb(stores: string[]): Promise<TkosDb> {
  const wanted = new Set<string>([...stores, QUEUE_STORE]);

  return new Promise((resolve, reject) => {
    // 1) Open at the current version (no version arg) to learn what exists.
    const probe = indexedDB.open(DB_NAME);

    probe.onerror = (): void => reject(probe.error ?? new Error("IndexedDB open failed"));
    probe.onblocked = (): void => reject(new Error("IndexedDB open blocked"));
    probe.onsuccess = (): void => {
      const db = probe.result;
      const missing = [...wanted].filter((n) => !db.objectStoreNames.contains(n));
      if (missing.length === 0) {
        resolve(wrap(db));
        return;
      }

      // 2) Some stores are missing → reopen at a higher version to add them.
      const nextVersion = db.version + 1;
      db.close();
      const up = indexedDB.open(DB_NAME, nextVersion);

      up.onupgradeneeded = (): void => {
        const udb = up.result;
        for (const name of wanted) {
          if (!udb.objectStoreNames.contains(name)) {
            udb.createObjectStore(name, { keyPath: "id" });
          }
        }
      };
      up.onsuccess = (): void => resolve(wrap(up.result));
      up.onerror = (): void => reject(up.error ?? new Error("IndexedDB upgrade failed"));
      up.onblocked = (): void => reject(new Error("IndexedDB open blocked"));
    };
  });
}

function wrap(db: IDBDatabase): TkosDb {
  function tx(store: string, mode: IDBTransactionMode): IDBObjectStore {
    return db.transaction(store, mode).objectStore(store);
  }

  function done(request: IDBRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      request.transaction!.oncomplete = (): void => resolve();
      request.transaction!.onerror = (): void => reject(request.error);
      request.transaction!.onabort = (): void => reject(request.error);
    });
  }

  return {
    getAll<T>(store: string): Promise<T[]> {
      return new Promise((resolve, reject) => {
        const r = tx(store, "readonly").getAll();
        r.onsuccess = (): void => resolve((r.result ?? []) as T[]);
        r.onerror = (): void => reject(r.error);
      });
    },

    get<T>(store: string, id: string): Promise<T | undefined> {
      return new Promise((resolve, reject) => {
        const r = tx(store, "readonly").get(id);
        r.onsuccess = (): void =>
          resolve(r.result === undefined ? undefined : (r.result as T));
        r.onerror = (): void => reject(r.error);
      });
    },

    put<T>(store: string, record: T): Promise<void> {
      return done(tx(store, "readwrite").put(record as unknown as object));
    },

    async putMany<T>(store: string, records: T[]): Promise<void> {
      if (records.length === 0) return;
      const s = tx(store, "readwrite");
      let last: IDBRequest | null = null;
      for (const r of records) {
        last = s.put(r as unknown as object);
      }
      // `last` is guaranteed non-null after the loop above.
      return done(last as IDBRequest);
    },

    remove(store: string, id: string): Promise<void> {
      return done(tx(store, "readwrite").delete(id));
    },

    clear(store: string): Promise<void> {
      return done(tx(store, "readwrite").clear());
    },
  };
}

export const SYNC_QUEUE_STORE = QUEUE_STORE;
